import { otherPagePatch } from '../designer/pageKinds';
import type { ModuleSpecPage } from '../designer/specTypes';
/**
 * UPT-2079 L11 (spec web `SPEC-UPT-2079-L11.md` §9) — réparation LOCALE d'une version de Module Spec pour le dépôt de démonstration : fonction pure, sans IA ni réseau,
 * qui reproduit les quatre corrections mécaniques de la normalisation étendue du serveur (rest, vague 135) sur une COPIE du document :
 *   - `workflow.state_key`  : un état avec `code` : `code` devient `key` (repris s'il manque, supprimé sinon), `key` ramené au motif `^[a-z][a-z0-9-]*$` ;
 *   - `page.description`    : une description de moins de 20 caractères est complétée à partir du libellé, du type de page et du nom du module ;
 *   - `module.domain`       : le domaine est ramené au motif `^[a-z][a-z0-9-]{1,30}$` (minuscules, tirets, 31 caractères) ;
 *   - `relation.duplicate`  : un lien dont le code existe déjà est retiré s'il est identique au premier, sinon son code reçoit un chiffre (`…app` → `…app2`, le code
 *                             d'un lien se termine par `[a-z0-9]+`).
 * Rend le document réparé et les corrections (`ModuleSpecRepairChange` : chemin de l'ÉLÉMENT dans le document AVANT réparation, nature, avant, après ; `after` vide = retiré).
 * UPT-2079 L12a : avec `coreFirst`, le socle d'abord et les noms déjà pris s'appliquent ensuite (`moduleSpecLocalCoreFirst` : objets du socle retirés, champs retypés,
 * liens retirés, objets renommés) ; leurs chemins restent ceux du document REÇU.
 * APPROXIMATION DE DÉMONSTRATION : le serveur reste la référence.
 */
import type { ModuleSpecDocument, ModuleSpecRepairChange } from '../data/moduleSpecContracts';
import { moduleSpecLocalCoreFirst } from './moduleSpecLocalCoreFirst';

type Json = Record<string, unknown>;

/** `$defs.domain` du schéma v1.1 et sa borne. */
export const DOMAIN_PATTERN = /^[a-z][a-z0-9-]{1,30}$/;
const DOMAIN_MAX = 31;
/** `$defs.workflow` : motif de la clé d'un état. */
export const STATE_KEY_PATTERN = /^[a-z][a-z0-9-]*$/;
/** `$defs.page.description.minLength`. */
export const PAGE_DESCRIPTION_MIN = 20;

const asRecord = (value: unknown): Json | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : null;
const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const ascii = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Un identifiant minuscule à tirets (`Nouveau dossier` → `nouveau-dossier`), commençant par une lettre ; vide si rien ne reste. */
const slugOf = (value: string): string =>
  ascii(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^[^a-z]+/, '')
    .replace(/-+$/g, '');

const labelOf = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  const record = asRecord(value);
  if (!record) return '';
  for (const key of ['fr', 'en', 'de', 'it', 'ar']) {
    const text = record[key];
    if (typeof text === 'string' && text.trim() !== '') return text.trim();
  }
  return '';
};

export interface LocalRepairResult {
  spec: ModuleSpecDocument;
  changes: ModuleSpecRepairChange[];
}

export interface LocalRepairOptions {
  /** Applique aussi le socle d'abord et les noms déjà pris (L12a) ; faux par défaut : les quatre corrections mécaniques de L11 seulement. */
  coreFirst?: boolean;
}

export function moduleSpecLocalRepair(input: ModuleSpecDocument, options: LocalRepairOptions = {}): LocalRepairResult {
  const spec = JSON.parse(JSON.stringify(input)) as ModuleSpecDocument;
  const changes: ModuleSpecRepairChange[] = [];
  if (Array.isArray(spec.pages)) spec.pages.forEach((page, index) => {
    if (page.kind !== 'other') return;
    Object.assign(page, otherPagePatch(page as unknown as ModuleSpecPage));
    changes.push({ kind: 'page.kind_other_migrated', path: '/pages/' + index + '/kind', before: 'other', after: 'custom' });
    spec.specVersion = '1.3';
  });

  // Domaine du module.
  const module = asRecord(spec.module);
  if (module && typeof module.domain === 'string' && !DOMAIN_PATTERN.test(module.domain)) {
    let domain = slugOf(module.domain).slice(0, DOMAIN_MAX).replace(/-+$/g, '');
    if (domain.length < 2) domain = slugOf(asString(module.code)).slice(0, DOMAIN_MAX).replace(/-+$/g, '');
    if (domain.length < 2) domain = 'module';
    changes.push({ path: '/module/domain', kind: 'module.domain', before: module.domain, after: domain });
    module.domain = domain;
  }

  // États de workflow : `code` devient `key`.
  const entities = asRecord(spec.entities) ?? {};
  Object.keys(entities).forEach((entityCode) => {
    const entity = asRecord(entities[entityCode]);
    asArray(entity?.fields).forEach((raw, index) => {
      const data = asRecord(asRecord(raw)?.data);
      if (data?.type !== 'store' || typeof data.entity !== 'string' || !entities[data.entity] || typeof data.valueField !== 'string' || data.valueField === 'id') return;
      changes.push({ path: ['/entities', entityCode, 'fields', index, 'data/valueField'].join('/'), kind: 'reference.identifier', before: data.valueField, after: 'id' });
      data.valueField = 'id';
    });
    const states = asArray(asRecord(asRecord(entities[entityCode])?.workflow)?.states);
    states.forEach((raw, index) => {
      const state = asRecord(raw);
      if (!state) return;
      const base = `/entities/${entityCode}/workflow/states/${index}`;
      if ('code' in state) {
        const code = asString(state.code);
        const hasKey = asString(state.key) !== '';
        const key = hasKey ? asString(state.key) : slugOf(code) || slugOf(labelOf(state.label)) || `etat-${index + 1}`;
        changes.push({ path: base, kind: 'workflow.state_key', before: code, after: hasKey ? '' : key });
        delete state.code;
        state.key = key;
      }
      if (typeof state.key === 'string' && state.key !== '' && !STATE_KEY_PATTERN.test(state.key)) {
        const key = slugOf(state.key) || `etat-${index + 1}`;
        changes.push({ path: base, kind: 'workflow.state_key', before: state.key, after: key });
        state.key = key;
      }
    });
  });

  // Descriptions d'écran trop courtes.
  const moduleName = labelOf(module?.name) || asString(module?.code);
  asArray(spec.pages).forEach((raw, index) => {
    const page = asRecord(raw);
    if (!page || typeof page.description !== 'string' || page.description.length >= PAGE_DESCRIPTION_MIN) return;
    const label = labelOf(page.label) || asString(page.key);
    const kind = asString(page.kind) || 'other';
    const description = `${label} : écran de type ${kind} du module ${moduleName || 'courant'}.`;
    changes.push({ path: `/pages/${index}/description`, kind: 'page.description', before: page.description, after: description });
    page.description = description;
  });

  // Liens en double.
  const relations = asArray(spec.relations);
  const firstByCode = new Map<string, unknown>();
  const kept: unknown[] = [];
  const taken = new Set(relations.map((raw) => asString(asRecord(raw)?.code)).filter((code) => code !== ''));
  // Index, dans le document REÇU, de chaque lien gardé (un lien identique retiré décale les suivants).
  const keptIndexes: number[] = [];
  relations.forEach((raw, index) => {
    const relation = asRecord(raw);
    const code = asString(relation?.code);
    if (!relation || code === '' || !firstByCode.has(code)) {
      if (relation && code !== '') firstByCode.set(code, raw);
      kept.push(raw);
      keptIndexes.push(index);
      return;
    }
    const path = `/relations/${index}`;
    if (JSON.stringify(firstByCode.get(code)) === JSON.stringify(raw)) {
      changes.push({ path, kind: 'relation.duplicate', before: code, after: '' });
      return;
    }
    let suffix = 2;
    while (taken.has(`${code}${suffix}`)) suffix += 1;
    const renamed = `${code}${suffix}`;
    taken.add(renamed);
    changes.push({ path, kind: 'relation.duplicate', before: code, after: renamed });
    relation.code = renamed;
    kept.push(relation);
    keptIndexes.push(index);
  });
  if (kept.length !== relations.length) spec.relations = kept;

  if (options.coreFirst) {
    changes.push(...moduleSpecLocalCoreFirst(spec, { relationIndex: (index) => keptIndexes[index] ?? index }));
  }

  return { spec, changes };
}
